/**
 * Centralized route constants.
 * Use these instead of hardcoding path strings across the app.
 */
export const ROUTES = {
  // Public
  LOGIN: '/login',
  SIGNUP: '/signup',
  VERIFY: '/verify',
  SETUP_COMPANY: '/setup-company',
  CHANGE_PASSWORD: '/change-password',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  ACCEPT_INVITE: '/accept-invite',
  ONBOARDING: '/onboarding',
  ONBOARDING_INTERVIEW: '/onboarding/interview',

  // App (protected)
  DASHBOARD: '/',
  /** Meu Dia — personal cockpit (deals to close, pending proposals, upcoming activities). */
  MEU_DIA: '/meu-dia',
  /** Setup & Configurações — hub that consolidates Account / Tenant / User settings. */
  SETUP: '/setup',
  /**
   * Equipe — team-usage dashboard (T5). Manager surface (tenant_admin / gestor /
   * diretor / account_owner + janus internal). Leaderboard + per-rep drill +
   * adoption rollup + export, consuming the BFF aggregator `GET /api/v1/team/usage`.
   */
  EQUIPE: '/equipe',
  PIPELINE: '/pipeline',
  PIPELINE_DETAIL: (id: string) => `/pipeline/${id}`,
  /** Central de Atividades — Cockpit de Tarefas & Agendamentos do Vendedor. */
  ACTIVITIES: '/activities',
  PROSPECTS: '/prospects',
  PROSPECT_DETAIL: (id: string) => `/prospects/${id}`,
  /**
   * Radar de Empresas (Radar 360 — F2). Monthly high-fit company
   * recommendations auto-created as leads. Gated behind the `radar` feature
   * flag + paid-plan check (free plans see the upsell state).
   */
  RADAR: '/radar',
  /** Autopilot — Fila de Aprovação HITL (Janus Signal). */
  AUTOPILOT: '/autopilot',
  /** Autopilot — Cockpit de Vidro (histórico auditável das ações enviadas). */
  AUTOPILOT_COCKPIT: '/autopilot/cockpit',
  /**
   * Autopilot — execução em LOTE: o que está rodando agora nos deals ativados
   * de uma vez. Os ids vão em `?deals=` (o destino do "Ativar Autopilot" da
   * seleção múltipla do Pipeline). Distinta de `/autopilot`, que é a fila de
   * aprovação: lá é "o que preciso aprovar", aqui é "como está andando".
   */
  AUTOPILOT_EXECUCOES: '/autopilot/execucoes',
  autopilotExecucoes: (dealIds: string[]) =>
    `/autopilot/execucoes?deals=${dealIds.join(',')}`,
  /**
   * Autopilot — "Automações ativas": lista PERSISTENTE, tenant-wide, de todo
   * deal com Autopilot ligado + estado + próximo passo. 4ª irmã de
   * fila/cockpit/execuções (auditoria UX+PM 2026-08-31) — NÃO é
   * `/autopilot/execucoes` generalizada: aquela é uma view efêmera pós-
   * ativação (`?deals=` de uma seleção), esta é o destino permanente de "meu
   * Autopilot está rodando?". Também NÃO vive dentro de "Agendamentos" —
   * uma cadência de Autopilot não tem hora marcada (avança por condição, não
   * por relógio), então forçar as duas no mesmo vocabulário de "próxima
   * execução" mentiria em metade das linhas.
   */
  AUTOPILOT_DEALS: '/autopilot/deals',
  /**
   * Equipe de Agentes — contratação, configuração e ensaio do Agente de
   * Mensageria (onda 1). Dark-launch: flag `agente_mensageria`
   * (`defaultEnabled:false`) + entitlement `agent_messaging` da conta.
   *
   * A fila de aprovação N1 e o recibo auditável NÃO moram aqui — são
   * `/autopilot` e `/autopilot/cockpit`, que já existem ponta a ponta. Esta
   * tela apenas LINKA para lá (breakdown §2.7: "a tela nova só ganha um link").
   */
  AGENTES: '/agentes',
  CONTAS: '/contas',
  CONTAS_DETAIL: (id: string) => `/contas/${id}`,
  /** Receita → Oportunidades (read-model do CRM, isolado da qualificação). */
  OPORTUNIDADES: '/oportunidades',
  /** Proposta / CPQ (SF-CPQ-001) — list of proposals. */
  PROPOSTAS: '/propostas',
  /** Contrato / CLM (SF-CLM-001) — list of contracts. */
  CONTRATOS: '/contratos',
  /** Contrato / CLM (SF-CLM-001) — single contract detail. */
  CONTRATO_DETAIL: (id: string) => `/contratos/${id}`,
  ALERTS: '/alerts',
  CONTACTS: '/contacts',
  INBOX: '/inbox',
  /**
   * Lote 1 do Hub de Comunicação Multicanal — visão tenant-wide, alimentada
   * por GET /api/v1/activity-cadences/overview (manager-tier).
   */
  CADENCIAS_MULTICANAL: '/cadencias-multicanal',
  /** Lote 1 do Hub — GET /api/v1/mkt/lead-sources/summary. */
  ORIGEM_LEADS: '/origem-leads',
  /**
   * Unified BDR+SDR workbench (Wave 3 docx). bdr/sdr both render the same
   * page; the legacy /bdr and /sdr paths still exist as redirects to keep
   * deep-links and analytics alive.
   */
  SALES_REP: '/sales-rep',
  /** @deprecated kept as redirect to SALES_REP. */
  BDR: '/bdr',
  /** @deprecated kept as redirect to SALES_REP. */
  SDR: '/sdr',
  /** SDR voice-agent cockpit (T-026): history, automation/agenda, analytics. */
  SDR_COCKPIT: '/sdr/cockpit',
  /**
   * Deep-link target for the "meeting_pending_assignment" platform
   * notification (action_url emitted by tenant-management). Redirects to the
   * cockpit Reuniões tab so the bell click lands on the meetings inbox.
   */
  SDR_REUNIOES: '/sdr/reunioes',
  EXECUTIVE: '/executive',
  PROFILE: '/profile',
  AGENT: '/agent',
  ADMIN: '/admin',
  ALLOWED_DOMAINS: '/admin/allowed-domains',
  ADMIN_ANALYTICS: '/admin/analytics',
  ADMIN_BILLING: '/admin/billing',
  ADMIN_TERRITORIES: '/admin/territories',
  ADMIN_SEATS: '/admin/seats',
  /**
   * Cross-Sell — bulk import shells (Onda 3 / XS-LOAD). Entry point dos
   * fluxos de importação em lote de clientes (commercial-accounts.xlsx) e do
   * catálogo de produtos (catalog.xlsx). Onda 3 entrega apenas o botão
   * "Baixar template"; o upload completo continua em /cross-sell/import.
   */
  ADMIN_IMPORT_CLIENTES: '/admin/import-clientes',
  ADMIN_IMPORT_CATALOGO: '/admin/import-catalogo',
  BILLING_CREDITS: '/billing/credits',
  ROI_CALCULATOR: '/analytics/roi',
  WIN_LOSS: '/analytics/win-loss',
  CALENDAR: '/calendar',
  /**
   * Sala de Guerra — role-aware command-center overview (Revenue Cockpit).
   * Manager/exec surface; scope is resolved upstream by the BFF/calendar from
   * the caller's role. Gated behind the `war_room` feature flag.
   */
  WAR_ROOM: '/war-room',
  /**
   * Cross-Sell (XS Fase 3) — bulk import wizard + per-job progress. Gated by
   * the `cross_sell` feature flag (Plus+) and gestor+ roles.
   */
  CROSS_SELL_IMPORT: '/cross-sell/import',
  CROSS_SELL_IMPORT_DETAIL: (jobId: string) => `/cross-sell/import/${jobId}`,
  /**
   * Cross-Sell campaigns (XS Fase 5) — list / wizard / funnel detail. Same gate
   * as the import wizard (`cross_sell` flag + gestor+ roles).
   */
  CROSS_SELL_CAMPAIGNS: '/cross-sell/campaigns',
  CROSS_SELL_CAMPAIGN_NEW: '/cross-sell/campaigns/new',
  CROSS_SELL_CAMPAIGN_DETAIL: (id: string) => `/cross-sell/campaigns/${id}`,
  TEMPLATES: '/templates',
  /**
   * Biblioteca de Modelos de Fluxo (programa flow-templates, Fase 1). Destino
   * PRÓPRIO de navegação — antes a `FlowTemplateLibrary` só existia embutida
   * como picker dentro do canvas do Autopilot / da cadência / da régua de
   * nutrição, sem rota nem entrada de menu: dava para USAR um modelo, nunca
   * para GERIR a biblioteca. Em pt-BR como as rotas de negócio mais novas
   * (`/contas`, `/oportunidades`), não em inglês como as legadas.
   */
  FLOW_TEMPLATES: '/modelos-de-fluxo',
  /**
   * Per-account marketing integrations hub: tenant admins bring their own
   * OAuth credentials per channel (Google Ads / Meta Ads / LinkedIn Ads)
   * or opt into demo mode. Primary entry point — the legacy
   * `/mkt/integrations` URL ainda funciona como alias (see App.tsx).
   */
  INTEGRATIONS: '/integracoes',
  /**
   * Legacy CRM integrations page (HubSpot/Salesforce sync). Kept under
   * a sub-path so deep-links and the settings recommendation cards keep
   * working while we converge on a single /integracoes hub.
   */
  CRM_INTEGRATIONS: '/integracoes/crm',
  /**
   * A tela ÚNICA do LinkedIn: a casa (de onde a sessao sai) + os dois metodos
   * de conexao. Substitui o par `linkedin` + `conector-residencial`, que eram
   * cards irmaos e escondiam a ordem — o navegador seguro sai PELA casa.
   */
  LINKEDIN_INTEGRATION: '/integracoes/linkedin',
  /**
   * @deprecated Conector Residencial como tela separada. Continua existindo
   * como REDIRECT para `/integracoes/linkedin#casa`: links antigos, prints de
   * suporte e a mensagem do admin que provisionou a casa seguem funcionando.
   */
  CONECTOR_RESIDENCIAL: '/integracoes/conector',
  /**
   * Signature-provider configuration wizard (Lead-to-Cash). BYO DocuSign vs
   * managed ZapSign — account-wide setting, gated behind the `contracts` flag.
   */
  SIGNATURE_CONFIG: '/integracoes/assinatura',
  /**
   * Live Login do LinkedIn — página FULL-SCREEN aberta em ABA NOVA (hosted,
   * estilo Unipile). Autenticada mas montada FORA do AppLayout (sem sidebar).
   * O `LinkedInIntegrationPanel` abre esta rota via `window.open`; ao capturar
   * o `li_at` a página avisa a aba de origem via postMessage e fecha sozinha.
   */
  LINKEDIN_LIVE_LOGIN: '/integracoes/linkedin/live-login',
  /**
   * GoTo telephony (BYO) connect + call-history page. Gated behind the
   * `telephony` feature flag + admin role.
   */
  TELEPHONY_INTEGRATIONS: '/integracoes/telefonia',
  /**
   * WhatsApp Business (Cloud API / Meta BSP) multi-tenant onboarding — Embedded
   * Signup connect + templates + per-tenant first-message selector. Dark-gated
   * by `FEATURE_WHATSAPP_CLOUD_API` (default OFF). Distinct from the MKT
   * WhatsApp Ads landing (`/integracoes/whatsapp`).
   */
  WHATSAPP_BUSINESS: '/integracoes/whatsapp-business',
  MKT: '/mkt',
  /**
   * Campaign Orchestration F1-3 — página de detalhe da campanha guarda-chuva
   * (KPIs reais do rollup v2, breakdown por canal, assets, publicar/pausar/
   * atribuir). Entrada: clique no nome na aba Campanhas.
   */
  MKT_CAMPAIGN_DETAIL: (id: string) => `/mkt/campaigns/${id}`,
  /**
   * Segmentos v2 (Marketing Hub v3 · W2, EF-2) — detalhe de UM segmento:
   * membros sem máscara (cursor), histórico de materializações, insights,
   * "Atualizar agora" e "Congelar como lista". Entrada: o nome na aba
   * Segmentos do /mkt. Com a flag `MKT_SEGMENTS_V2_ENABLED` desligada as
   * abas dizem "indisponível" — a página continua abrindo.
   */
  MKT_SEGMENT_DETAIL: (id: string) => `/mkt/segments/${id}`,
  /**
   * Campanhas de e-mail (Marketing Hub v3 · W3, item 7) — o disparo agendado
   * para um segmento.
   *
   * Endereço PRÓPRIO, e não uma aba das réguas, porque o que a pessoa vem
   * fazer aqui é outro trabalho: escolher público + conteúdo + horário UMA
   * vez. No backend a campanha É uma régua (`trigger_type='scheduled_blast'`,
   * um passo `email`) — não há segundo motor —, mas o vocabulário da tela é
   * o de campanha, e misturá-la na lista de réguas faria a coluna "status"
   * significar duas coisas.
   *
   * Com `MKT_EMAIL_CAMPAIGNS_ENABLED` desligada as rotas respondem 404 e a
   * tela diz "indisponível" — a página continua abrindo.
   */
  MKT_EMAIL_CAMPAIGNS: '/mkt/email-campaigns',
  MKT_EMAIL_CAMPAIGN_NEW: '/mkt/email-campaigns/new',
  MKT_EMAIL_CAMPAIGN_DETAIL: (id: string) => `/mkt/email-campaigns/${id}`,
  /**
   * Saúde do e-mail (Marketing Hub v3 · W4, 7b — a pendência da W2) — KPIs
   * de entregabilidade, bounce por tipo, domínios que mais bounçam,
   * campanhas abaixo da média, estado do provedor, autenticação do domínio
   * (reuso do card da W3) e o limiar do alerta. Abriga também a aba
   * "Supressões" (lista/filtros/criação manual/reativar `gestor+`) — não há
   * `/settings/email` nesta base para hospedá-la separadamente.
   *
   * Com `MKT_EMAIL_HEALTH_ENABLED` desligada a rota responde 404 e a tela
   * diz "indisponível" — a página continua abrindo.
   */
  MKT_EMAIL_HEALTH: '/mkt/email-health',
  /** @deprecated alias of INTEGRATIONS — kept so old links keep working. */
  MKT_INTEGRATIONS: '/mkt/integrations',
  MKT_INTEGRATION_CALLBACK: (provider: string) => `/mkt/integrations/${provider}/callback`,
  MKT_INTEGRATION_DETAIL: (connId: string) => `/mkt/integrations/${connId}`,
  /** Detalhe de UMA campanha de canal: analítico + leads + comparativo. */
  MKT_CHANNEL_CAMPAIGN_DETAIL: (connId: string, campId: string) =>
    `/mkt/integrations/${connId}/campanhas/${campId}`,
  /**
   * Wave 4 — dedicated WhatsApp Ads management page (templates +
   * conversations + analytics + settings). Reachable from the
   * generic connection detail page when provider == 'whatsapp_ads'.
   */
  MKT_INTEGRATION_WHATSAPP: (connId: string) => `/mkt/integrations/${connId}/whatsapp`,
  /** Aposta 2 — Incrementality testing list + detail. */
  MKT_INCREMENTALITY: '/mkt/incrementality',
  MKT_COHORTS: '/mkt/cohorts',
  /** Central de Recomendações — o estrategista de canais. */
  MKT_RECOMMENDATIONS: '/mkt/recommendations',
  /** Aposta 3 — outcome-based pricing dashboard + list. */
  MKT_OUTCOMES: '/mkt/outcomes',
  /**
   * GC-05.1 — fila de aprovação de publicação (a saída do `pending_approval`).
   *
   * O caminho `/admin/mkt/approvals/...` NÃO é escolha de layout: o
   * janus-marketing já emite a notificação com
   * `action_url = /admin/mkt/approvals/{job_id}`
   * (`_enter_pending_approval`), e até agora esse link não tinha rota
   * nenhuma no SPA. Estas constantes existem para que a fila tenha UM
   * vocabulário — quem exibe "Aguardando aprovação" (assistente de
   * publicação, painel de outcomes, detalhe da campanha) aponta para
   * cá em vez de inventar um segundo endereço.
   *
   * Mudar estes valores quebra o link da notificação; se um dia mudarem,
   * mudam junto com `_enter_pending_approval`.
   */
  MKT_APPROVALS: '/admin/mkt/approvals',
  MKT_APPROVAL_DETAIL: (jobId: string) => `/admin/mkt/approvals/${jobId}`,
  PRIVACY: '/privacidade',
  KNOWLEDGE: '/knowledge',
  KNOWLEDGE_VIDEO: (id: string) => `/knowledge/video/${id}`,

  // B2B Account Layer (onda 3a)
  ROLLUP: '/rollup',
  AUTH_PICK_ACCOUNT: '/auth/pick-account',

  // Support (Sprint Support-1)
  SUPPORT_INBOX: '/admin/support/tickets',
  SUPPORT_TICKET_DETAIL: (id: string) => `/admin/support/tickets/${id}`,

  // Settings
  SETTINGS_ONBOARDING: '/settings/onboarding',
  SETTINGS_COMPANY_CONTEXT: '/settings/company-context',
  SETTINGS_PIPELINE: '/settings/pipeline',
  SETTINGS_VOICE_AGENT: '/settings/voice-agent',
  SETTINGS_PRODUCTS: '/settings/products',
  SETTINGS_DOCUMENT_IDENTITY: '/settings/document-identity',
  SETTINGS_BRANDING: '/settings/branding',

  // Public (no auth)
  REQUEST_ACCESS: '/solicitar-acesso',
  WAITLIST: '/waitlist',
  /**
   * Public proposal accept link (SF-CPQ-001, US-CPQ-07). No auth — the
   * external decisor opens this with a one-time token; mounted OUTSIDE the
   * AppLayout / auth guard. NOT feature-gated client-side.
   */
  PROPOSTA_ACEITE: (token: string) => `/proposta/aceite/${token}`,
} as const;
